/**
 * Visitor Tracking Service
 * Tracks website visits and counts unique visitors
 * Improved version with better tracking on each visit
 */

export const VisitorService = {
  // Initialize visitor tracking
  init: () => {
    try {
      const visitorId = VisitorService.getOrCreateVisitorId();
      const today = new Date().toISOString().split('T')[0];
      
      // Always record the visit (even if same visitor today)
      VisitorService.recordVisit(visitorId, today);
      
      // Debug logging
      if (window.__DEBUG_TRACKING) {
        console.log(`✅ Visitor tracked: ${visitorId.substring(0, 30)}...`);
        console.log(`📊 Today's date: ${today}`);
        console.log(`👁️ Today's visitors: ${VisitorService.getTodayVisitors()}`);
      }
    } catch (error) {
      console.error('Error initializing visitor tracking:', error);
    }
  },

  // Get or create unique visitor ID (session-based - one per browser session)
  getOrCreateVisitorId: () => {
    let visitorId = sessionStorage.getItem('visitorId');
    
    if (!visitorId) {
      // Create unique ID: timestamp + random string
      visitorId = `v${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('visitorId', visitorId);
      
      // Log new visitor
      if (window.__DEBUG_TRACKING) {
        console.log(`🆕 New visitor created: ${visitorId}`);
      }
    }
    
    return visitorId;
  },

  // Record a visit (unique count per day)
  recordVisit: (visitorId, date) => {
    try {
      const key = `visits_${date}`;
      let visits = JSON.parse(localStorage.getItem(key) || '[]');
      
      // Add this visitor if not already in today's list
      if (!visits.includes(visitorId)) {
        visits.push(visitorId);
        localStorage.setItem(key, JSON.stringify(visits));
        
        if (window.__DEBUG_TRACKING) {
          console.log(`➕ Added to today's count: ${visitorId}`);
        }
      }
      
      // Record page view with timestamp (always record, even for same visitor)
      const pageViewKey = 'pageViews';
      let pageViews = JSON.parse(localStorage.getItem(pageViewKey) || '[]');
      pageViews.push({
        visitorId,
        date,
        timestamp: new Date().toISOString(),
        page: window.location.pathname,
      });
      
      // Keep only last 1000 page views
      if (pageViews.length > 1000) {
        pageViews = pageViews.slice(-1000);
      }
      localStorage.setItem(pageViewKey, JSON.stringify(pageViews));
      
      if (window.__DEBUG_TRACKING) {
        console.log(`📄 Page view recorded: ${window.location.pathname}`);
      }
    } catch (error) {
      console.error('Error recording visit:', error);
    }
  },

  // Get visitors for a specific date
  getVisitorsForDate: (date) => {
    try {
      const key = `visits_${date}`;
      const visits = JSON.parse(localStorage.getItem(key) || '[]');
      return visits.length;
    } catch (error) {
      console.error('Error getting visitors:', error);
      return 0;
    }
  },

  // Get total visitors for last 7 days
  getVisitorsLastWeek: () => {
    try {
      let totalVisitors = new Set();
      
      for (let i = 0; i < 7; i++) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        const key = `visits_${dateStr}`;
        const visits = JSON.parse(localStorage.getItem(key) || '[]');
        visits.forEach((v) => totalVisitors.add(v));
      }
      
      return totalVisitors.size;
    } catch (error) {
      console.error('Error getting visitors:', error);
      return 0;
    }
  },

  // Get total visitors for last month (30 days)
  getVisitorsLastMonth: () => {
    try {
      let totalVisitors = new Set();
      
      for (let i = 0; i < 30; i++) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        const key = `visits_${dateStr}`;
        const visits = JSON.parse(localStorage.getItem(key) || '[]');
        visits.forEach((v) => totalVisitors.add(v));
      }
      
      return totalVisitors.size;
    } catch (error) {
      console.error('Error getting last month visitors:', error);
      return 0;
    }
  },

  // Get today's visitor count
  getTodayVisitors: () => {
    const today = new Date().toISOString().split('T')[0];
    return VisitorService.getVisitorsForDate(today);
  },

  // Get total page views today
  getTodayPageViews: () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const pageViews = JSON.parse(localStorage.getItem('pageViews') || '[]');
      return pageViews.filter((pv) => pv.date === today).length;
    } catch (error) {
      console.error('Error getting page views:', error);
      return 0;
    }
  },

  // Get total all-time page views
  getTotalPageViews: () => {
    try {
      const pageViews = JSON.parse(localStorage.getItem('pageViews') || '[]');
      return pageViews.length;
    } catch (error) {
      console.error('Error getting total page views:', error);
      return 0;
    }
  },

  // Get daily visitor statistics
  getDailyStats: (days = 7) => {
    try {
      const stats = [];
      
      for (let i = days - 1; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        const visitors = VisitorService.getVisitorsForDate(dateStr);
        
        stats.push({
          date: dateStr,
          visitors,
          day: new Date(dateStr).toLocaleDateString('en-US', { weekday: 'short' }),
        });
      }
      
      return stats;
    } catch (error) {
      console.error('Error getting daily stats:', error);
      return [];
    }
  },

  // Get all visitor IDs for debugging
  getDebugInfo: () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const visitorsToday = JSON.parse(localStorage.getItem(`visits_${today}`) || '[]');
      const currentVisitorId = sessionStorage.getItem('visitorId');
      
      return {
        today,
        visitorsToday,
        currentVisitorId,
        totalToday: visitorsToday.length,
        pageViewsToday: VisitorService.getTodayPageViews(),
        totalPageViews: VisitorService.getTotalPageViews(),
      };
    } catch (error) {
      console.error('Error getting debug info:', error);
      return {};
    }
  },

  // Enable debug mode in console
  enableDebug: () => {
    window.__DEBUG_TRACKING = true;
    console.log('🐛 Visitor tracking debug mode ENABLED');
    console.log('Debug Info:', VisitorService.getDebugInfo());
  },

  // Disable debug mode
  disableDebug: () => {
    window.__DEBUG_TRACKING = false;
    console.log('🐛 Visitor tracking debug mode DISABLED');
  },

  // Clear all visitor data (for testing)
  clearData: () => {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        if (key.startsWith('visits_') || key === 'pageViews') {
          localStorage.removeItem(key);
        }
      });
      sessionStorage.removeItem('visitorId');
      console.log('🗑️ All visitor tracking data cleared');
    } catch (error) {
      console.error('Error clearing data:', error);
    }
  },
};

export default VisitorService;
